import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Upload, FileText, Sparkles, Check, X, ArrowLeft, Download, Loader2, Eye, FileSearch, Info } from 'lucide-react';
import { api, formatINR, useAuth } from '../lib/store';
import { logger } from '../lib/logger';
import { creditsToastOptions } from '../lib/platform';

export default function AIImportPage() {
  const navigate = useNavigate();
  const { canManageProducts } = useAuth();
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('Laminates');
  const [loading, setLoading] = useState(false);
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [importStatus, setImportStatus] = useState(null);
  
  // Vision mode settings
  const [useVisionMode, setUseVisionMode] = useState(true);
  const [brandHint, setBrandHint] = useState('');
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(3);  // Default to 3 pages to avoid timeout
  const [pdfInfo, setPdfInfo] = useState(null);
  const [pagesProcessed, setPagesProcessed] = useState(0);

  // Restrict access
  if (!canManageProducts) {
    return (
      <div className="min-h-screen bg-heda-cream flex items-center justify-center px-4">
        <Card className="rounded-2xl bg-white shadow-none border-heda-line max-w-md">
          <CardContent className="py-12 text-center">
            <X className="w-12 h-12 mx-auto text-red-700 mb-4" />
            <p className="font-display text-lg font-bold text-heda-ink">Access Denied</p>
            <p className="text-sm text-heda-sub mt-1">Only Admin and Manager can access AI Import</p>
            <Button onClick={() => navigate('/')} className="rounded-xl mt-4">Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setExtractedProducts([]);
      setSelectedProducts(new Set());
      setImportStatus(null);
      setPdfInfo(null);
      // Start with just 3 pages to avoid timeout - user can increase later
      setStartPage(1);
      setEndPage(3);
    } else {
      toast.error('Please select a PDF file');
    }
  };

  const handleExtract = async () => {
    if (!file) {
      toast.error('Please select a PDF file first');
      return;
    }

    // Warn if page range is too large for vision mode (may timeout)
    const pageCount = endPage - startPage + 1;
    if (useVisionMode && pageCount > 5) {
      const proceed = window.confirm(
        `Processing ${pageCount} pages with Vision mode may take ${pageCount * 10}-${pageCount * 15} seconds and could timeout.\n\nRecommendation: Start with 3-5 pages first to test.\n\nProceed anyway?`
      );
      if (!proceed) return;
    }

    setLoading(true);
    setExtractedProducts([]);
    setImportStatus(null);
    setPagesProcessed(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let response;
      if (useVisionMode) {
        // Use vision-based extraction
        formData.append('category', category);
        formData.append('brand_hint', brandHint);
        formData.append('start_page', startPage.toString());
        formData.append('end_page', endPage.toString());
        formData.append('batch_size', '2');  // Reduced batch size for stability
        
        response = await api.extractPdfVision(formData);
      } else {
        // Use text-based extraction
        response = await api.extractPdf(formData, category);
      }
      
      if (response.data.success) {
        setExtractedProducts(response.data.products);
        setSelectedProducts(new Set(response.data.products.map((_, i) => i)));
        setPagesProcessed(response.data.pages_processed || 0);
        toast.success(response.data.message || `Extracted ${response.data.total_extracted} products from ${response.data.pages_processed || 'unknown'} pages!`);
      } else {
        toast.error(response.data.message || 'Extraction failed');
      }
    } catch (error) {
      logger.error('Extraction error:', error);
      const status = error.response?.status;
      if (status === 402 || status === 403) {
        // Out of AI credits / not in the plan: the message says so as it is.
        toast.error(error.response.data?.detail || 'AI credits unavailable', creditsToastOptions());
      } else {
        toast.error('Failed to extract: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProduct = (index) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedProducts(new Set(extractedProducts.map((_, i) => i)));
  };

  const handleDeselectAll = () => {
    setSelectedProducts(new Set());
  };

  const handleImport = async () => {
    const productsToImport = extractedProducts.filter((_, i) => selectedProducts.has(i));
    
    if (productsToImport.length === 0) {
      toast.error('Please select at least one product to import');
      return;
    }

    setLoading(true);

    try {
      const response = await api.bulkImportProducts(productsToImport);
      
      setImportStatus({
        success: true,
        imported: response.data.imported,
        errors: response.data.errors
      });
      
      toast.success(response.data.message || `Imported ${response.data.imported} products!`);
      
    } catch (error) {
      logger.error('Import error:', error);
      toast.error('Failed to import: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-heda-cream">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-heda-line bg-heda-cream/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 md:px-8 md:py-4">
          <button type="button" onClick={() => navigate(-1)} className="heda-pill" data-testid="ai-import-back-btn">
            <ArrowLeft size={13} /> Back
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-heda-gold" />
            <h1 className="font-display text-[20px] font-bold text-heda-ink">AI PDF Import</h1>
            <span className="rounded-full bg-heda-gold-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-gold">BETA</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-8">
        {/* Upload Section */}
        <Card className="rounded-2xl bg-white shadow-none border-heda-line">
          <CardHeader className="pb-2">
            <CardTitle className="text-heda-ink flex items-center gap-2">
              <Upload className="w-5 h-5" /> Upload Price List PDF
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mode Toggle */}
            <div className="flex items-center justify-between p-3 bg-heda-cream/60 rounded-xl border border-heda-line">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-heda-olive-dark" />
                <div>
                  <p className="font-semibold text-heda-ink text-sm">Vision Mode (Recommended)</p>
                  <p className="text-xs text-heda-sub">Uses AI vision to read tables & images accurately</p>
                </div>
              </div>
              <Switch
                checked={useVisionMode}
                onCheckedChange={setUseVisionMode}
                data-testid="vision-mode-toggle"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Category Select */}
              <div>
                <label className="heda-label">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="rounded-xl bg-white border-heda-line" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Laminates">Laminates</SelectItem>
                    <SelectItem value="Highlighters">Highlighters</SelectItem>
                    <SelectItem value="Plywood">Plywood</SelectItem>
                    <SelectItem value="Hardware">Hardware</SelectItem>
                    <SelectItem value="Veneers">Veneers</SelectItem>
                    <SelectItem value="Adhesives">Adhesives</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Brand Hint (Vision mode) */}
              {useVisionMode && (
                <div>
                  <label className="heda-label">Brand Hint (Optional)</label>
                  <Input
                    value={brandHint}
                    onChange={(e) => setBrandHint(e.target.value)}
                    placeholder="e.g., ARCHIDPLY"
                    className="rounded-xl bg-white border-heda-line"
                    data-testid="brand-hint-input"
                  />
                </div>
              )}

              {/* Page Range (Vision mode) */}
              {useVisionMode && (
                <>
                  <div>
                    <label className="heda-label">Start Page</label>
                    <Input
                      type="number"
                      min={1}
                      value={startPage}
                      onChange={(e) => setStartPage(Math.max(1, parseInt(e.target.value) || 1))}
                      className="rounded-xl bg-white border-heda-line"
                      data-testid="start-page-input"
                    />
                  </div>
                  <div>
                    <label className="heda-label">End Page</label>
                    <Input
                      type="number"
                      min={startPage}
                      value={endPage}
                      onChange={(e) => setEndPage(Math.max(startPage, parseInt(e.target.value) || startPage))}
                      className="rounded-xl bg-white border-heda-line"
                      data-testid="end-page-input"
                    />
                  </div>
                </>
              )}
            </div>

            {/* File Input */}
            <div>
              <label className="heda-label">PDF File</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="pdf-file-input"
                  />
                  <div className="flex items-center gap-2 px-3 py-2 border border-heda-line rounded-xl bg-white">
                    <FileText className="w-4 h-4 text-heda-sub" />
                    <span className={`text-sm ${file ? 'text-heda-ink' : 'text-heda-mute'}`}>
                      {file ? file.name : 'Choose PDF file...'}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleExtract}
                  disabled={!file || loading}
                  className="rounded-xl bg-heda-olive hover:bg-heda-olive-dark text-white"
                  data-testid="extract-button"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Extracting...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-1" /> Extract with AI</>
                  )}
                </Button>
              </div>
            </div>

            {/* Instructions */}
            <div className="p-3 bg-heda-gold-tint rounded-xl border border-heda-gold/30">
              <p className="text-sm text-heda-ink flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>How it works:</strong> Upload your PDF price list → AI extracts products with codes & rates 
                  {useVisionMode && " using vision (page-by-page image analysis for tables)"}
                  {!useVisionMode && " using text extraction"} → Review & Import
                  {useVisionMode && (
                    <span className="block mt-1 text-xs">
                      <strong className="text-red-700">⚠️ Start with 3-5 pages max!</strong> Processing takes ~10 sec/page. Larger ranges may timeout.
                    </span>
                  )}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Extracted Products */}
        {extractedProducts.length > 0 && (
          <Card className="rounded-2xl bg-white shadow-none border-heda-line">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-heda-ink" data-testid="extracted-count">
                    Extracted Products ({extractedProducts.length})
                  </CardTitle>
                  {pagesProcessed > 0 && (
                    <p className="text-xs text-heda-sub mt-1">From {pagesProcessed} pages processed</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" size="sm" onClick={handleSelectAll} data-testid="select-all-btn">
                    Select All
                  </Button>
                  <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" size="sm" onClick={handleDeselectAll} data-testid="deselect-all-btn">
                    Deselect All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Products Table */}
              <div className="border border-heda-line rounded-xl overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="heda-thead sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left w-10">
                          <input
                            type="checkbox"
                            checked={selectedProducts.size === extractedProducts.length}
                            onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                            className="w-4 h-4"
                          />
                        </th>
                        <th className="px-2 py-2 text-left">Brand</th>
                        <th className="px-2 py-2 text-left">Code</th>
                        <th className="px-2 py-2 text-left">Name/Finish</th>
                        {category === 'Plywood' && <th className="px-2 py-2 text-left">Thickness</th>}
                        <th className="px-2 py-2 text-left">Size</th>
                        <th className="px-2 py-2 text-right">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extractedProducts.map((product, idx) => (
                        <tr
                          key={idx}
                          className={`border-t border-heda-line ${selectedProducts.has(idx) ? 'bg-heda-olive-tint' : 'bg-white'}`}
                          data-testid={`product-row-${idx}`}
                        >
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              checked={selectedProducts.has(idx)}
                              onChange={() => handleToggleProduct(idx)}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="px-2 py-2 font-semibold">{product.brand_name}</td>
                          <td className="px-2 py-2">
                            <Badge variant="outline" className="rounded-full font-mono text-xs">{product.product_code}</Badge>
                          </td>
                          <td className="px-2 py-2 text-heda-sub text-xs">{product.finish || product.product_name || '-'}</td>
                          {category === 'Plywood' && <td className="px-2 py-2 font-mono text-xs">{product.thickness || '-'}</td>}
                          <td className="px-2 py-2 text-xs">{product.size}</td>
                          <td className="px-2 py-2 text-right font-mono font-semibold text-heda-olive-dark">
                            {formatINR(product.rate)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Import Button */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-heda-line">
                <p className="text-sm text-heda-sub">
                  {selectedProducts.size} of {extractedProducts.length} products selected
                </p>
                <Button
                  onClick={handleImport}
                  disabled={selectedProducts.size === 0 || loading}
                  className="rounded-xl bg-heda-ink hover:bg-black text-white"
                  data-testid="import-button"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importing...</>
                  ) : (
                    <><Download className="w-4 h-4 mr-1" /> Import {selectedProducts.size} Products</>
                  )}
                </Button>
              </div>

              {/* Import Status */}
              {importStatus && (
                <div className={`mt-4 p-3 rounded-xl border ${importStatus.success ? 'bg-heda-olive-tint border-emerald-700' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {importStatus.success ? (
                      <Check className="w-5 h-5 text-emerald-700" />
                    ) : (
                      <X className="w-5 h-5 text-red-700" />
                    )}
                    <span className={`font-semibold ${importStatus.success ? 'text-emerald-700' : 'text-red-700'}`}>
                      {importStatus.success ? `Successfully imported ${importStatus.imported} products!` : 'Import failed'}
                    </span>
                  </div>
                  {importStatus.errors?.length > 0 && (
                    <div className="mt-2 text-sm text-red-700">
                      Errors: {importStatus.errors.slice(0, 5).join(', ')}
                      {importStatus.errors.length > 5 && ` ... and ${importStatus.errors.length - 5} more`}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && extractedProducts.length === 0 && (
          <Card className="rounded-2xl bg-white shadow-none border-heda-line">
            <CardContent className="py-12 text-center">
              <Loader2 className="w-12 h-12 mx-auto text-heda-gold animate-spin mb-4" />
              <p className="text-lg font-semibold text-heda-ink">
                {useVisionMode ? 'AI Vision is analyzing your PDF...' : 'AI is extracting text from your PDF...'}
              </p>
              <p className="text-sm text-heda-sub mt-1">
                {useVisionMode 
                  ? `Processing pages ${startPage} to ${endPage} (~${(endPage - startPage + 1) * 5} seconds)`
                  : 'This may take 30-60 seconds for large files'}
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
